<template>
  <div class="source-content graphics">
    <div class="filter-bar">
      <search-filter
        :value="search"
        :options="filteredTags"
        :selected="selectedTags"
        data-test="producer-assets-displayElements-searchFilter"
        @input="changeGraphicsSearch"
        @update:selected="onUpdateSelectedTags"
      />
    </div>
    <!-- No graphic templates after searching -->
    <div v-if="!isLoading && !total" class="text-center">
      <h3 class="margin-bottom-md">
        No graphics found
      </h3>

      <button
        class="tb-btn tb-btn-contained medium-em"
        data-entity="producer-assets-resetTemplateSearch"
        data-test="producer-assets-displayElements-resetSearch"
        @click="clearTags"
      >
        Reset Search
      </button>
    </div>

    <div
      ref="sourceList"
      class="source-list"
      data-test="producer-assets-displayElements-list"
    >
      <draggable
        :list="filteredTemplates"
        :clone="clone"
        :sort="false"
        animation="200"
        :group="{ name: 'timeline', pull: 'clone', put: false }"
        @start="startDrag"
        @end="$store.dispatch('clearDragType')"
        revert-on-spill="true"
      >
        <div
          v-for="template in filteredTemplates"
          :key="template._id"
          class="source-item graphic"
          :data-template-id="template._id"
          data-entity="producer-assets-template"
          :data-test="`producer-assets-element:${template._id}`"
        >
          <div class="tb-checkbox">
            <input
              type="checkbox"
              :id="template._id"
              :value="template"
              v-model="selected"
              data-entity="producer-assets-toggleTemplateSelection"
              :data-test="`producer-assets-element-checkBox:${template._id}`"
            >

            <label class="checkbox" :for="template._id" />
          </div>

          <div class="source-item-wrapper">
            <img class="preview margin-right-xs" :src="template.preview_url" :data-test="`producer-assets-element-preview:${template._id}`" />

            <div class="source-item-info-wrapper">
              <div class="source-title title-text" :title="template.title" :data-test="`producer-assets-element-title:${template._id}`">
                {{ template.title }}
              </div>

              <div
                :class="{
                  'source-item-tags': true,
                  'margin-top-xxs': template.tags?.length,
                }"
                :data-test="`producer-assets-element-tags:${template._id}`"
              >
                <span
                  v-for="tag in template.tags"
                  :key="tag"
                  class="tag"
                  :data-test="`producer-assets-element-tags:${template._id}_${tag}`"
                >
                  {{ tag.toLowerCase() }}
                </span>
              </div>
            </div>

            <div class="source-actions margin-left-xxs">
              <button
                class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                title="Add to playlist"
                data-entity="producer-assets-addTemplateToPlaylist"
                :data-test="`producer-assets-element-addToPlaylist:${template._id}`"
                @click="addToPlaylist(template, 'Graphic')"
              >
                <span class="icon icon-plus" />
              </button>
            </div>
          </div>
        </div>
      </draggable>

      <div ref="loadTrigger" class="load-trigger">
        <div v-if="isLoading" class="loading loading--inline" data-test="producer-assets-displayElements-loading">
          <i class="icon icon-spinner"></i>
        </div>
      </div>
    </div>

    <div
      v-if="selected.length"
      class="flex content-end items-center padding-md"
      data-test="producer-assets-selected"
    >
      <p
        class="text-primary margin-bottom-none margin-right-sm"
        data-test="producer-assets-selected-count"
      >
        {{ selected.length }} selected
      </p>

      <tgb-split-button
        :options="splitButtonOptions"
        :disabled="!selected.length"
        split-button-label="Add group"
        data-entity="producer-assets-addSelectedTemplatesToPlaylist"
        data-test="producer-assets-addElementsToPlaylist"
        @click="addSelectedToPlaylist('Graphics')"
      >
        Add to playlist
      </tgb-split-button>
    </div>
  </div>
</template>

<script>
import * as uuid from 'uuid';
import { mapState, mapGetters, mapActions } from 'vuex';
import { pick } from 'lodash-es';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import tabMixins from '../../mixins/tab-mixins';
import SplitButton from '../../components/split-button/TgbSplitButton.vue';
import ConduitEventsService from '../../services/conduitEventsService';

import toast from '../../components/toast/toast';

export default {
  mixins: [tabMixins],

  components: {
    'tgb-split-button': SplitButton,
    SearchFilter,
  },

  data() {
    return {
      showTemplatePicker: false,
      selectedGraphic: null,
      selectedTags: [],
    };
  },

  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  watch: {
    templates() {
      if (!this.$refs.sourceList || !this.templates.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },

  created() {
    this.$store.dispatch('getTemplates').catch((err) => {
      console.error('Error loading templates:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load templates.',
      });
    });
  },

  beforeDestroy() {
    this.$store.dispatch('setGraphics', []);
  },

  computed: {
    ...mapState({
      isLoading: (state) => state.graphics.isLoading,
      search: (state) => state.graphics.search,
      templates: (state) => state.graphics.templates,
      lookupTable: (state) => state.graphics.templatesLookupTable,
      total: (state) => state.graphics.total,
    }),

    filteredTags() {
      const tagSet = new Set();

      this.templates.forEach((template) => {
        (template.tags || []).forEach(tag => tagSet.add(tag.toLowerCase()));
      });

      return Array.from(tagSet).sort();
    },

    filteredTemplates() {
      return this.templates.filter(template => {
        if (this.selectedTags.length) {
          const templateTags = (template.tags || []).map(tag => tag.toLowerCase());
          return this.selectedTags.some(tag => templateTags.includes(tag));
        }
        
        return true;
      });
    },
    
    ...mapGetters(['sortedGraphicsTags']),

    splitButtonOptions() {
      const groups = this.$store.state.production.production.timeline.filter((item) => (
        item.layout === 'graphics'
      ));

      const options = groups.map((group) => ({
        key: group.timeline_id,
        label: group.title,
        onClick: () => {
          this.addSelectedToGroup('Graphics', group.timeline_id);
        },
      }));

      if (options.length) {
        options.push({ divider: true });
      }

      // Option to create a new group and add to that instead
      options.push({
        key: 'new',
        label: '+ New Group',
        onClick: async () => {
          const posts = this.selected.map(this.clone);

          await this.$store.dispatch('addGraphicsGroup', { posts });

          this.selected = [];


          await this.$store.dispatch('getTimelineTemplates');
          await this.$store.dispatch('generateShortUrls', posts.map((item) => item.timeline_id));
          this.$store.dispatch('trackSportradarInGameElement', posts.map((item) => item.timeline_id));
          this.$store.dispatch('updateTimeline');

          ConduitEventsService.track(
              'producer.created.group-from-menu',
              {
                production_id: this.production._id,
              },
              {
                ownerId: this.production.owner,
                userId: this.$store.state.user.id,
                isStaff: this.$store.getters.isStaff,
              },
          );

          ConduitEventsService.track(
              'producer.added.items-to-playlist',
              {
                type: 'Graphic',
                count: posts?.length || 1,
                production_id: this.production._id,
                component: 'GraphicTemplates.vue',
                method: 'addSelectedToGroup',
                items: posts,
              },
              {
                ownerId: this.production.owner,
                userId: this.$store.state.user.id,
                isStaff: this.$store.getters.isStaff,
              },
          );
        },
      });

      return options;
    },
  },

  methods: {
    ...mapActions([
      'changeGraphicsSearch',
    ]),

    onUpdateSelectedTags(tags) {
      this.selectedTags = tags.map(t => t.toLowerCase());
    },

    /**
     * Force fetch graphics again, jic more graphics were added since user entered producer.
     */
    reloadGraphics() {
      this.$store.dispatch('resetTemplates');
      this.$store.dispatch('getTemplates');
    },

    /**
    * Attempt to load more graphic templates
    */
    loadMore() {
      if (!this.isLoading && this.$store.getters.hasMoreTemplates) {
        this.$store.dispatch('incrementTemplatesPage');
      }
    },

    /**
    * Remove all current tags, reset page to 1, and fetch templates again.
    */
    clearTags() {
      this.selectedTags = [];
      this.$store.dispatch('resetSearch');
    },

    startDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === parseInt(templateId, 10));
      this.$store.dispatch('setDragType', {
        type: 'graphic',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },

    clone(original) {
      const clone = pick(original, ['title', 'owner', 'meta']);

      clone.template_id = original._id;

      clone.timeline_id = uuid.v1();
      clone.displayTime = 5000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.layout = 'graphic';
      clone.posts = [{ timeline_id: clone.timeline_id }];
      clone.fields = {};

      if (original.socialData) {
        clone.socialData = original.socialData;
      }

      return clone;
    },
  },
};
</script>
